<template>
	<div id="clientCustomizer" class="w-100">
		<MediaLibraryModal v-on:mediaInsert="insertPhoto($event)" v-on:hideMediaModal="hideMediaModal()" />
		<DataSelectModal :type="dataSelectType" v-on:selectDataItem="selectDataItem($event)" />
		<FigmaAiModal :setting="figmaAiSetting" :client-id="client.id" v-on:updatePreview="updateThemeSettingsPreview()" />
		<div class="customizer-wrapper position-relative jc-customizer">
			<div
				v-if="sidebarMinimized"
				class="sidebar-unminimizer jc-customizer-unminimizer d-flex justify-content-center align-items-center position-fixed start-0 top-0 opacity-25 bg-dark cursor-pointer"
				@click="sidebarMinimize"
			>
				<i class="fa-solid fa-arrow-right"></i>
			</div>
			<!-- Customizer sidebar -->
			<div class="customizer-sidebar" :class="sidebarMinimized && 'sidebar-minimized'">
				<!-- Customizer Heading -->
				<div class="customizer-heading">
					<div class="customizer-heading__top">
						<button type="button" class="customizer-heading__collapse" @click="sidebarMinimize" aria-label="Hide customizer sidebar">
							<i class="fa-solid fa-arrow-left"></i>
						</button>
						<small class="customizer-heading__meta">You are customizing</small>
						<router-link class="customizer-back-link" :to="'/juniper/clients/edit/' + this.$route.params.id">Back to Edit</router-link>
					</div>
					<h5>{{ client.name }}</h5>
				</div>
				<!-- #Customizer Heading -->
				<!-- Theme Active -->
				<div v-if="sidebarMenu" class="theme">
					<h5>Theme</h5>
					<div class="form-group">
						<select @change="changeTheme()" class="form-select" v-model="themeOptions.theme_active">
							<option v-for="(theme, i) in themes" :value="theme" :key="i">{{ theme }}</option>
						</select>
					</div>
				</div>
				<!-- #Theme Active -->

				<!-- Sidebar Menu -->
				<div class="sidebar-menu" v-if="sidebarMenu">
					<div class="list-group">
						<button type="button" @click="showSettings('theme')" class="list-group-item list-group-item-action customizer-nav-btn">Theme Settings</button>
						<button type="button" @click="showSettings('header')" class="list-group-item list-group-item-action customizer-nav-btn">Header Settings</button>
						<button type="button" @click="showSettings('homepage')" class="list-group-item list-group-item-action customizer-nav-btn">Homepage Sections</button>
						<button type="button" class="list-group-item list-group-item-action customizer-nav-btn" disabled>Menus 🧑‍💻</button>
						<button type="button" @click="showSettings('footer')" class="list-group-item list-group-item-action customizer-nav-btn">Footer Settings</button>
						<button type="button" @click="showSettings('weather')" class="list-group-item list-group-item-action customizer-nav-btn">Weather</button>
						<button type="button" @click="showSettings('heygov')" class="list-group-item list-group-item-action customizer-nav-btn">HeyGov</button>
						<button type="button" @click="showSettings('userway')" class="list-group-item list-group-item-action customizer-nav-btn">UserWay</button>
					</div>
				</div>
				<!-- #Sidebar Menu -->

				<!-- Theme Settings -->
				<div v-if="activeSidebarSettings === 'theme'" class="settings-part theme-settings">
					<div class="settings-part-header">
						<button class="customizer-back-btn" @click="backToSidebarMenu()">
							<font-awesome-icon :icon="['fas', 'chevron-left']" class="" />
						</button>
						<div class="title">
							<small>Customizing</small>
							<h5>Theme</h5>
						</div>
					</div>
					<div class="settings-part-content">
						<!-- Theme Sections Wrapper -->
						<div class="footer-wrapper">
							<!-- Theme Content -->
							<div class="mt-3" v-if="themeOptions.theme_settings_json && themeOptions.theme_settings_json.length > 0">
								<h5>Default Settings</h5>
								<div class="form-group mb-2" v-for="(variable, index) in themeOptions.theme_settings_json" :key="variable.name">
									<CustomizerProperty
										:data="variable"
										:index="index"
										:googleFonts="googleFonts"
										:type="'theme-settings'"
										:themeOptions="themeOptions.theme_settings_json"
										v-on:updatePreview="updatePreview($event)"
										v-on:showMediaLibraryModal="showMediaModal($event)"
										v-on:showFigmaAiModal="showFigmaAiModal($event)"
									/>
								</div>
							</div>
							<div v-else class="text-center mt-3">No Theme Settings for this theme 😔</div>
						</div>
						<!-- #Theme Sections Wrapper -->
					</div>
				</div>

				<!-- Homepage Settings -->
				<div v-if="activeSidebarSettings === 'homepage'" class="settings-part homepage-settings">
					<div class="settings-part-header">
						<button class="customizer-back-btn" @click="backToSidebarMenu()">
							<font-awesome-icon :icon="['fas', 'chevron-left']" class="" />
						</button>
						<div class="title">
							<small>Customizing</small>
							<h5>Homepage</h5>
						</div>
					</div>
					<div class="settings-part-content">
						<!-- Homepage Sections Wrapper -->
						<div v-if="homepageSections.length > 0" class="homepage-sections-wrapper">
							<h5>Homepage sections</h5>
							<draggable v-model="homepageSections" @change="updateHomepageSectionsOrder()">
								<transition-group class="list-group">
									<div
										class="list-group-item list-group-item-action"
										:class="{ 'not-visible': !section.visible }"
										v-for="(section, i) in homepageSections"
										:key="section.id"
									>
										<button @click="section.visible = !section.visible" class="btn p-0 m-0">
											<font-awesome-icon v-if="section.visible" :icon="['fas', 'eye']" class="me-2" />
											<font-awesome-icon v-else :icon="['fas', 'eye-slash']" class="me-2" />
										</button>
										{{ section.name }}
										<button
											v-if="section.variables && section.variables.length > 0"
											class="btn btn-primary float-end p-2"
											@click="openHomepageSectionSettings(i)"
											style="border-radius: 50%"
											title="This section have mutable variables"
										>
											<font-awesome-icon style="font-size: 10px" :icon="['fas', 'edit']" class="float-end" />
										</button>
									</div>
								</transition-group>
							</draggable>
							<div class="section-variables">
								<div v-for="(section, i) in homepageSections" :key="section.id">
									<div v-if="section.variables && section.variables.length > 0 && section.settings" class="section-settings border mt-2 rounded-2 p-2 bg-white">
										<div class="d-flex justify-content-between align-items-center">
											<h5 class="p-0 m-0">{{ section.name }} settings</h5>
											<button @click="closeHomepageSectionSettings()" class="btn p-0 m-0">x</button>
										</div>

										<div class="form-group mb-2" v-for="(variable, index) in section.variables" :key="variable.name">
											<CustomizerProperty
												:data="variable"
												:index="index"
												:googleFonts="googleFonts"
												:type="'homepage'"
												:sectionIndex="i"
												:themeOptions="themeOptions.theme_settings_json"
												v-on:updatePreview="updatePreview($event)"
												v-on:showMediaLibraryModal="showMediaModal($event)"
												v-on:showDataSelectModal="showDataSelectModal($event)"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
						<!-- #Homepage Sections Wrapper -->
					</div>
				</div>
				<!-- #Homepage Settings -->

				<!-- Header Settings -->
				<div v-if="activeSidebarSettings === 'header'" class="settings-part header-settings">
					<div class="settings-part-header">
						<button class="customizer-back-btn" @click="backToSidebarMenu()">
							<font-awesome-icon :icon="['fas', 'chevron-left']" class="" />
						</button>
						<div class="title">
							<small>Customizing</small>
							<h5>Header</h5>
						</div>
					</div>
					<div class="settings-part-content">
						<!-- Header Sections Wrapper -->

						<div class="headers-wrapper">
							<!-- Header Template-->

							<div class="d-flex justify-content-between align-items-center">
								<h6 class="p-0 m-0">Template</h6>
								<div class="form-group" style="width: 180px">
									<select @change="getHeader(themeOptions.theme_active)" class="form-select" v-model="themeOptions.theme_header_active">
										<option v-for="(header, i) in headers" :value="header" :key="i + '-header'">{{ header }}</option>
									</select>
								</div>
							</div>
							<!-- #Header Template -->

							<!-- Header Content -->
							<div
								class="mt-3"
								v-if="
									allHeaders[themeOptions.theme_header_active] &&
									allHeaders[themeOptions.theme_header_active].variables &&
									allHeaders[themeOptions.theme_header_active].variables.length > 0
								"
							>
								<h5>Header content</h5>
								<div class="form-group mb-2" v-for="(variable, index) in allHeaders[themeOptions.theme_header_active].variables" :key="variable.name">
									<CustomizerProperty
										:data="variable"
										:index="index"
										:googleFonts="googleFonts"
										:type="'header'"
										:sectionIndex="0"
										:themeOptions="themeOptions.theme_settings_json"
										v-on:updatePreview="updatePreview($event)"
										v-on:showMediaLibraryModal="showMediaModal($event)"
									/>
								</div>
							</div>
							<div v-else class="text-center mt-3">No settings for this header 😔</div>
							<!-- #Header Content -->
							<!-- #Alternative Headers -->
							<div v-if="headers.length > 1">
								<div class="settings-part-header mt-5">
									<div class="title">
										<small>Customizing</small>
										<h5>Alternative Headers</h5>
									</div>
								</div>
								<div class="mt-3 mb-2">
									<select class="form-select" v-model="alternativeHeaderSelected">
										<option :value="null">-- Select --</option>
										<option v-for="(header, i) in headers.filter(h => h !== themeOptions.theme_header_active)" :value="header" :key="i + '-header'">
											{{ header }}
										</option>
									</select>
								</div>
								<div id="alternativeHeaderSelected" v-if="alternativeHeaderSelected">
									<div class="my-3">
										<label for="alternativeHeaderPagesSelect">Choose pages wich you want to use selected alternative header</label>
										<multiselect
											v-model="allHeaders[alternativeHeaderSelected].pages"
											id="alternativeHeaderPagesSelect"
											label="label"
											track-by="value"
											placeholder="Type to search"
											open-direction="bottom"
											:options="alternativeHeaderPages"
											:multiple="true"
											:searchable="true"
											:loading="isLoadingPageSearch"
											:internal-search="false"
											:clear-on-select="false"
											:close-on-select="false"
											:options-limit="300"
											:limit="3"
											:limit-text="limitSearchPagesText"
											:max-height="600"
											:show-no-results="true"
											:hide-selected="false"
											@search-change="getAlternativeHeaderPages"
										>
											<template #tag="{ option, remove }"
												><span class="custom__tag"
													><span>{{ option.label }}</span
													><span class="custom__remove" @click="remove(option)">❌</span></span
												></template
											>
											<template #clear="props">
												<div
													class="multiselect__clear"
													v-if="
														allHeaders[alternativeHeaderSelected] &&
														allHeaders[alternativeHeaderSelected].pages &&
														allHeaders[alternativeHeaderSelected].pages.length
													"
													@mousedown.prevent.stop="clearAll(props.search)"
												></div>
											</template>
											<template #noResult>
												<span>Oops! No elements found. Consider changing the search query.</span>
											</template>
										</multiselect>
									</div>
									<div
										class="mt-3"
										v-if="
											alternativeHeaderSelected &&
											allHeaders[alternativeHeaderSelected] &&
											allHeaders[alternativeHeaderSelected].variables &&
											allHeaders[alternativeHeaderSelected].variables.length > 0
										"
									>
										<h5>Alternative Header content</h5>
										<div class="form-group mb-2" v-for="(variable, index) in allHeaders[alternativeHeaderSelected].variables" :key="variable.name">
											<CustomizerProperty
												:data="variable"
												:index="index"
												:googleFonts="googleFonts"
												:type="'header'"
												:sectionIndex="0"
												:themeOptions="themeOptions.theme_settings_json"
												v-on:updatePreview="updatePreview($event)"
												v-on:showMediaLibraryModal="showMediaModal($event)"
											/>
										</div>
									</div>
									<div v-else class="text-center mt-3">No settings for this header 😔</div>
								</div>
							</div>
							<!-- #Alternative Headers -->
						</div>
						<!-- #Footer Sections Wrapper -->
					</div>
				</div>
				<!-- #Footer Settings -->

				<!-- Footer Settings -->
				<div v-if="activeSidebarSettings === 'footer'" class="settings-part footer-settings">
					<div class="settings-part-header">
						<button class="customizer-back-btn" @click="backToSidebarMenu()">
							<font-awesome-icon :icon="['fas', 'chevron-left']" class="" />
						</button>
						<div class="title">
							<small>Customizing</small>
							<h5>Footer</h5>
						</div>
					</div>
					<div class="settings-part-content">
						<!-- Footer Sections Wrapper -->
						<div class="footer-wrapper">
							<!-- Footer Template-->
							<div class="d-flex justify-content-between align-items-center">
								<h6 class="p-0 m-0">Template</h6>
								<div class="form-group" style="width: 180px">
									<select @change="getFooter(themeOptions.theme_active)" class="form-select" v-model="themeOptions.theme_footer_active">
										<option v-for="(footer, i) in footers" :value="footer" :key="i + 'footer'">{{ footer }}</option>
									</select>
								</div>
							</div>
							<!-- #Footer Template -->

							<!-- Footer Content -->
							<div class="mt-3" v-if="footer.variables && footer.variables.length > 0">
								<h5>Footer content</h5>
								<div class="form-group mb-2" v-for="(variable, index) in footer.variables" :key="variable.name">
									<CustomizerProperty
										:data="variable"
										:index="index"
										:googleFonts="googleFonts"
										:type="'footer'"
										:sectionIndex="0"
										:themeOptions="themeOptions.theme_settings_json"
										v-on:updatePreview="updatePreview($event)"
										v-on:showMediaLibraryModal="showMediaModal($event)"
									/>
								</div>
							</div>
							<div v-else class="text-center mt-3">No settings for this footer 😔</div>
							<!-- #Footer Content -->
							<!-- #Alternative Footers -->
							<div v-if="footers.length > 1">
								<div class="settings-part-header mt-5">
									<div class="title">
										<small>Customizing</small>
										<h5>Alternative Footers</h5>
									</div>
								</div>
								<div class="mt-3 mb-2">
									<select class="form-select" v-model="alternativeFooterSelected">
										<option :value="null">-- Select --</option>
										<option v-for="(header, i) in headers.filter(h => h !== themeOptions.theme_footer_active)" :value="header" :key="i + '-header'">
											{{ header }}
										</option>
									</select>
								</div>
								<div id="alternativeFooterSelected" v-if="alternativeFooterSelected">
									<div class="my-3">
										<label for="alternativeFooterPagesSelect">Choose pages wich you want to use selected alternative header</label>
										<multiselect
											v-model="allFooters[alternativeFooterSelected].pages"
											id="alternativeFooterPagesSelect"
											label="label"
											track-by="value"
											placeholder="Type to search"
											open-direction="bottom"
											:options="alternativeFooterPages"
											:multiple="true"
											:searchable="true"
											:loading="isLoadingPageSearch"
											:internal-search="false"
											:clear-on-select="false"
											:close-on-select="false"
											:options-limit="300"
											:limit="3"
											:limit-text="limitSearchPagesText"
											:max-height="600"
											:show-no-results="true"
											:hide-selected="false"
											@search-change="getAlternativeFooterPages"
										>
											<template #tag="{ option, remove }"
												><span class="custom__tag"
													><span>{{ option.label }}</span
													><span class="custom__remove" @click="remove(option)">❌</span></span
												></template
											>
											<template #clear="props">
												<div
													class="multiselect__clear"
													v-if="
														allFooters[alternativeFooterSelected] &&
														allFooters[alternativeFooterSelected].pages &&
														allFooters[alternativeFooterSelected].pages.length
													"
													@mousedown.prevent.stop="clearAll(props.search)"
												></div>
											</template>
											<template #noResult>
												<span>Oops! No elements found. Consider changing the search query.</span>
											</template>
										</multiselect>
									</div>
									<div
										class="mt-3"
										v-if="
											alternativeFooterSelected &&
											allFooters[alternativeFooterSelected] &&
											allFooters[alternativeFooterSelected].variables &&
											allFooters[alternativeFooterSelected].variables.length > 0
										"
									>
										<h5>Alternative Header content</h5>
										<div class="form-group mb-2" v-for="(variable, index) in allFooters[alternativeFooterSelected].variables" :key="variable.name">
											<CustomizerProperty
												:data="variable"
												:index="index"
												:googleFonts="googleFonts"
												:type="'header'"
												:sectionIndex="0"
												:themeOptions="themeOptions.theme_settings_json"
												v-on:updatePreview="updatePreview($event)"
												v-on:showMediaLibraryModal="showMediaModal($event)"
											/>
										</div>
									</div>
									<div v-else class="text-center mt-3">No settings for this header 😔</div>
								</div>
							</div>
							<!-- #Alternative Headers -->
						</div>
						<!-- #Footer Sections Wrapper -->
					</div>
				</div>
				<!-- #Footer Settings -->

				<!-- Weather Settings -->
				<div v-if="activeSidebarSettings === 'weather'" class="settings-part weather-settings">
					<div class="settings-part-header">
						<button class="customizer-back-btn" @click="backToSidebarMenu()">
							<font-awesome-icon :icon="['fas', 'chevron-left']" class="" />
						</button>
						<div class="title">
							<small>Customizing</small>
							<h5>Weather</h5>
						</div>
					</div>
					<div class="settings-part-content">
						<div class="footer-wrapper">
							<div class="text-center mt-3">No settings for this weather 😔</div>
						</div>
					</div>
				</div>
				<!-- #Weather Settings -->

				<!-- HeyGov Settings -->
				<div v-if="activeSidebarSettings === 'heygov'" class="settings-part heygov-settings">
					<div class="settings-part-header">
						<button class="customizer-back-btn" @click="backToSidebarMenu()">
							<font-awesome-icon :icon="['fas', 'chevron-left']" class="" />
						</button>
						<div class="title">
							<small>Customizing</small>
							<h5>HeyGov</h5>
						</div>
					</div>
					<div class="settings-part-content">
						<div class="footer-wrapper">
							<div class="card">
								<div class="card-body">
									<div class="form-group mb-3">
										<div class="form-check">
											<input class="form-check-input" type="checkbox" v-model="heygovButton.show" id="displayHeygovButton" />
											<label class="form-check-label" for="displayHeygovButton"> Display HeyGov Button </label>
										</div>
									</div>

									<div class="form-group mb-3">
										<label for="">Button Text</label>
										<input type="text" v-model="heygovButton.text" class="form-control" />
									</div>

									<div class="form-group mb-3">
										<label for="">Button Position</label>
										<select @change="handleHGButtonPosition()" class="form-select" v-model="heygovButton.position">
											<option value="bottom-right">Bottom right</option>
											<option value="bottom-left">Bottom left</option>
											<option value="top-left">Top left</option>
											<option value="top-right">Top right</option>
										</select>
									</div>

									<div class="form-group mb-3" v-if="heygovButton.left != 'auto'">
										<label for="">Left</label>
										<input type="text" v-model="heygovButton.left" class="form-control" />
									</div>
									<div class="form-group mb-3" v-if="heygovButton.right != 'auto'">
										<label for="">Right</label>
										<input type="text" v-model="heygovButton.right" class="form-control" />
									</div>
									<div class="form-group mb-3" v-if="heygovButton.bottom != 'auto'">
										<label for="">Bottom</label>
										<input type="text" v-model="heygovButton.bottom" class="form-control" />
									</div>
									<div class="form-group mb-3" v-if="heygovButton.top != 'auto'">
										<label for="">Top</label>
										<input type="text" v-model="heygovButton.top" class="form-control" />
									</div>
									<div class="form-group mb-3">
										<label for="">Features</label>
										<input type="text" v-model="heygovButton.features" class="form-control" />
									</div>
									<div class="form-group mb-3">
										<label for="">Mode</label>
										<select class="form-select" v-model="heygovButton.mode">
											<option value="">None</option>
											<option value="chat">Chat</option>
											<option value="menu">Menu</option>
										</select>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<!-- #HeyGov Settings -->

				<!-- UserWay Settings -->
				<div v-if="activeSidebarSettings === 'userway'" class="settings-part userway-settings">
					<div class="settings-part-header">
						<button class="customizer-back-btn" @click="backToSidebarMenu()">
							<font-awesome-icon :icon="['fas', 'chevron-left']" class="" />
						</button>
						<div class="title">
							<small>Customizing</small>
							<h5>UserWay</h5>
						</div>
					</div>
					<div class="settings-part-content">
						<div class="footer-wrapper">
							<div class="text-center mt-3">
								<div class="card">
									<div class="card-body">
										<div class="form-group mb-3">
											<label for="">Button Position</label>
											<select @change="handleUWButtonPosition()" class="form-select" v-model="userwayButton.position">
												<option value="bottom-right">Bottom right</option>
												<option value="bottom-left">Bottom left</option>
												<option value="top-left">Top left</option>
												<option value="top-right">Top right</option>
											</select>
										</div>
										<div class="form-group mb-3" v-if="userwayButton.left != 'auto'">
											<label for="">Left</label>
											<input type="text" v-model="userwayButton.left" class="form-control" />
										</div>
										<div class="form-group mb-3" v-if="userwayButton.right != 'auto'">
											<label for="">Right</label>
											<input type="text" v-model="userwayButton.right" class="form-control" />
										</div>
										<div class="form-group mb-3" v-if="userwayButton.bottom != 'auto'">
											<label for="">Bottom</label>
											<input type="text" v-model="userwayButton.bottom" class="form-control" />
										</div>
										<div class="form-group mb-3" v-if="userwayButton.top != 'auto'">
											<label for="">Top</label>
											<input type="text" v-model="userwayButton.top" class="form-control" />
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<!-- #UserWay Settings -->

				<div class="update">
					<button :disabled="updateLoading || loadingPage" @click="saveClientThemeSettings()" class="btn btn-primary w-100 customizer-update-btn">
						<font-awesome-icon :icon="['fas', updateLoading ? 'spinner' : 'save']" :class="[updateLoading ? 'fa-spin' : '']" />
						Update
					</button>
					<button :disabled="staticLoading || loadingPage" @click="generateStatic()" class="btn btn-outline-light w-100 mt-2 customizer-secondary-btn">
						<font-awesome-icon :icon="[staticLoading ? 'fas' : 'fab', staticLoading ? 'spinner' : 'html5']" :class="[staticLoading ? 'fa-spin' : '']" />
						Generate Static
					</button>

					<button data-bs-toggle="modal" data-bs-target="#chooseCustomizationModal" class="btn btn-outline-light w-100 mt-4 customizer-secondary-btn" v-if="authUser.email.includes('srdjan')">
						<font-awesome-icon :icon="['fas', 'cloud-download-alt']" />
						Get homepage configuration
					</button>
				</div>
			</div>
			<!-- #Customizer sidebar -->

			<!-- Customizer HTML Content Preview -->
			<div class="customizer-content" :class="sidebarMinimized && 'sidebar-minimized'">
				<div v-if="loadingPage" class="customizer-content-loader">
					Loading
					<font-awesome-icon :class="'fa-spin ms-2'" :icon="['fas', 'spinner']" />
				</div>
				<div class="customizer-preview-shell">
					<div v-html="'<style>' + settingsCss + '</style>'"></div>
					<div v-html="assetsCss"></div>
					<div v-html="'<style>' + themeCss + '</style>'"></div>
					<div v-html="'<style>' + fontsCss + '</style>'"></div>
					<div v-html="'<style>' + headerCss + '</style>'"></div>
					<div v-html="'<style>' + homepageCss + '</style>'"></div>
					<div v-html="header.html"></div>
					<template v-for="section in homepageSections">
						<section :key="section.id + 'div'" :id="section.id" v-if="section.visible">
							<div v-html="section.content"></div>
						</section>
					</template>
					<div v-html="'<style>' + footerCss + '</style>'"></div>
					<div v-html="footer.html"></div>
					<div
						v-if="heygovButton.show"
						class="heygov-widget-button"
						:style="{ left: heygovButton.left, right: heygovButton.right, top: heygovButton.top, bottom: heygovButton.bottom }"
						:class="heygovButton.position"
					>
						{{ heygovButton.text }}
					</div>
					<div :style="{ left: userwayButton.left, right: userwayButton.right, top: userwayButton.top, bottom: userwayButton.bottom }" class="userway-button">
						<img style="width: 100%" src="https://cdn.userway.org/widgetapp/images/body_wh.svg" alt="" />
					</div>
				</div>
				<!-- <div>
					<iframe width="100%" style="height:100vh" :srcdoc="websiteData"></iframe>
				</div> -->
			</div>
			<!-- #Customizer HTML Content Preview -->
		</div>
		<!-- Modal -->
		<div class="modal fade" id="chooseCustomizationModal" tabindex="-1" aria-labelledby="chooseCustomizationModalLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="chooseCustomizationModalLabel">Customization Data</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="alert alert-light rounded" role="alert">
							<h4 class="alert-heading text-black">Choose client customization data to apply!</h4>
							<small>
								Here we can choose homepage customization to apply from other clients (if we need similar website with existing client). This functionality will
								copy all necessery files exept <code>images</code> they need to be added after.🧑‍💻
							</small>
						</div>
						<div class="clients-wrapper">
							<div class="d-flex justify-content-between align-items-center mb-2" v-for="dClient in clients" :key="dClient.id">
								<div class="client-data fs-5">
									{{ dClient.name }}
									<span v-if="dClient.id == client.id" class="badge bg-primary ms-2">active</span>
								</div>
								<div class="client-actions">
									<button class="btn btn-primary btn-sm" @click="selectCustomizerData(dClient.id)">Choose</button>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
						<button type="button" @click="applyChangedData()" class="btn btn-primary">Apply data on selected customizer settings</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'
import draggable from 'vuedraggable'
import CustomizerProperty from '@/components/juniper/customizer/CustomizerProperty'
import MediaLibraryModal from '@/components/juniper/media/MediaLibraryModal'
import DataSelectModal from '../../../components/juniper/customizer/modals/DataSelectModal.vue'
import FigmaAiModal from '../../../components/juniper/customizer/modals/FigmaAiModal.vue'
import { Modal } from 'bootstrap'
import fonts from '../clients/include/fonts.json'

//import restApi from '../../../api/index'

export default {
	name: 'ClientCustomizer',
	data() {
		return {
			loading: false,
			staticLoading: false,
			updateLoading: false,
			sidebarMenu: true,
			activeSidebarSettings: '',
			client: {},
			themes: [],
			headers: [],
			footers: [],
			themeOptions: {
				theme_active: 'default',
				theme_homepage_active: 'default',
				theme_homepage_sections_json: [],
				theme_footer_active: 'default',
				theme_footer_json: [],
				theme_settings_json: [],
			},
			settingsCss: ':root{--primary-color:#00a08d;--secondary-color:#002447;--tertiary-color: #cfeae7;}',
			themeCss: '',
			assetsCss: '',
			homepageCss: '',
			header: '',
			headerCss: '',
			homepageSections: [],
			footer: '',
			footerCss: '',
			mainCss: '',
			fontsCss: '',
			updateFooter: false,
			updateHeader: false,
			googleFonts: fonts,
			websiteData: '',
			themeJs: '',
			jqueryScript: '',
			showMediaLibrary: false,
			$mediaModal: null,
			mediaElement: null,
			dataSelectType: '',
			dataSelectItem: {},
			figmaAiSetting: null,
			clients: [],
			loadingPage: true,
			sidebarMinimized: false,
			heygovButton: {
				show: true,
				text: 'Report an issue',
				position: 'bottom-right',
				left: 'auto',
				right: '15px',
				top: 'auto',
				bottom: '15px',
				mode: '',
			},
			userwayButton: {
				position: 'bottom-right',
				left: 'auto',
				right: '15px',
				top: 'auto',
				bottom: '15px',
			},
			allHeaders: {},
			alternativeHeaderSelected: null,
			alternativeHeaderPages: [],
			selectedAlternativeHeaderPages: [],
			allFooters: {},
			alternativeFooterSelected: null,
			alternativeFooterPages: [],
			selectedAlternativeFooterPages: [],
			isLoadingPageSearch: false,
		}
	},
	components: { draggable, CustomizerProperty, MediaLibraryModal, DataSelectModal, FigmaAiModal },
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
		homepageSectionsWatch() {
			return Object.assign({}, this.homepageSections)
		},
	},

	mounted: function () {
		this.$mediaModal = new Modal('#mediaLibraryModal')
		this.$dataSelectModal = new Modal('#dataSelectModal')
		this.$figmaAiModal = new Modal('#figmaAiModal')
		window.setInterval(() => {
			// Interval
		}, 5000)
	},

	watch: {},

	async created() {
		await this.attachScripts()
		this.getClients()
		this.getClient()
	},

	methods: {
		executeCustomizerScripts() {
			const scripts = document.querySelectorAll('script[data-customizer="customizerScript"]')
			console.log('scripts', scripts)
			scripts.forEach(script => {
				try {
					const code = script.innerHTML.trim()
					if (code) {
						new Function(code)()
					}
				} catch (error) {
					console.error('Greška pri izvršavanju skripta:', error)
				}
			})
		},

		reloadCustomizerScript() {
			this.executeCustomizerScripts()
		},

		async attachScripts() {
			await this.loadJquery()
			await this.loadMarquee()
			await this.loadFontAwesome()
			await this.loadOwl2Css()
			await this.loadOwl2Js()
			await this.loadSlickCss()
			await this.loadSlickJs()
			await this.loadSwiperCss()
			await this.loadSwiperJs()
			await this.loadMask()
		},

		// Load jQuery in VUE
		loadJquery() {
			return new Promise(resolve => {
				let scriptEl = document.createElement('script')
				scriptEl.src = 'https://code.jquery.com/jquery-3.6.3.min.js'
				scriptEl.type = 'text/javascript'
				scriptEl.integrity = 'sha256-pvPw+upLPUjgMXY0G+8O0xUf+/Im1MZjXxxgOcBQBXU='
				scriptEl.crossOrigin = 'anonymus'
				document.getElementsByTagName('head')[0].appendChild(scriptEl)
				scriptEl.onload = function () {
					resolve()
				}
			})
		},

		loadMarquee() {
			return new Promise(resolve => {
				let scriptMq = document.createElement('script')
				scriptMq.src = 'https://cdn.jsdelivr.net/npm/jquery.marquee@1.6.0/jquery.marquee.min.js'
				scriptMq.type = 'text/javascript'
				document.getElementsByTagName('head')[0].appendChild(scriptMq)
				scriptMq.onload = function () {
					resolve()
				}
			})
		},

		loadFontAwesome() {
			return new Promise(resolve => {
				let linkFa = document.createElement('link')
				linkFa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.6.0/css/all.min.css'
				linkFa.integrity = 'sha512-Kc323vGBEqzTmouAECnVceyQqyqdsSiqLQISBL29aUW4U/M7pSPA/gEUZQqv1cwx4OnYxTxve5UMg5GT6L4JJg=='
				linkFa.crossOrigin = 'anonymus'
				linkFa.referrerpolicy = 'no-referrer'
				linkFa.rel = 'stylesheet'
				document.getElementsByTagName('head')[0].appendChild(linkFa)
				linkFa.onload = function () {
					resolve()
				}
			})
		},

		loadOwl2Css() {
			return new Promise(resolve => {
				let linkOw = document.createElement('link')
				linkOw.href = 'https://cdnjs.cloudflare.com/ajax/libs/OwlCarousel2/2.3.4/assets/owl.carousel.min.css'
				linkOw.integrity = 'sha512-tS3S5qG0BlhnQROyJXvNjeEM4UpMXHrQfTGmbQ1gKmelCxlSEBUaxhRBj/EFTzpbP4RVSrpEikbmdJobCvhE3g=='
				linkOw.crossOrigin = 'anonymus'
				linkOw.referrerpolicy = 'no-referrer'
				linkOw.rel = 'stylesheet'
				document.getElementsByTagName('head')[0].appendChild(linkOw)
				linkOw.onload = function () {
					resolve()
				}
			})
		},

		loadOwl2Js() {
			return new Promise(resolve => {
				let scriptOw = document.createElement('script')
				scriptOw.src = 'https://cdnjs.cloudflare.com/ajax/libs/OwlCarousel2/2.3.4/owl.carousel.min.js'
				scriptOw.type = 'text/javascript'
				scriptOw.integrity = 'sha512-bPs7Ae6pVvhOSiIcyUClR7/q2OAsRiovw4vAkX+zJbw3ShAeeqezq50RIIcIURq7Oa20rW2n2q+fyXBNcU9lrw=='
				scriptOw.crossOrigin = 'anonymus'
				scriptOw.referrerpolicy = 'no-referrer'
				document.getElementsByTagName('head')[0].appendChild(scriptOw)
				scriptOw.onload = function () {
					resolve()
				}
			})
		},

		loadSlickCss() {
			return new Promise(resolve => {
				let linkOw = document.createElement('link')
				linkOw.href = '//cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.css'
				linkOw.rel = 'stylesheet'
				document.getElementsByTagName('head')[0].appendChild(linkOw)
				linkOw.onload = function () {
					resolve()
				}
			})
		},

		loadSlickJs() {
			return new Promise(resolve => {
				let scriptOw = document.createElement('script')
				scriptOw.src = '//cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.min.js'
				scriptOw.type = 'text/javascript'
				document.getElementsByTagName('head')[0].appendChild(scriptOw)
				scriptOw.onload = function () {
					resolve()
				}
			})
		},

		loadSwiperCss() {
			return new Promise(resolve => {
				let linkSw = document.createElement('link')
				linkSw.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css'
				linkSw.rel = 'stylesheet'
				document.getElementsByTagName('head')[0].appendChild(linkSw)
				linkSw.onload = function () {
					resolve()
				}
			})
		},

		loadSwiperJs() {
			return new Promise(resolve => {
				let scriptSw = document.createElement('script')
				scriptSw.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js'
				scriptSw.type = 'text/javascript'
				document.getElementsByTagName('head')[0].appendChild(scriptSw)
				scriptSw.onload = function () {
					resolve()
				}
			})
		},

		loadMask() {
			return new Promise(resolve => {
				let scriptMask = document.createElement('script')
				scriptMask.src = 'https://cdnjs.cloudflare.com/ajax/libs/jquery.mask/0.9.0/jquery.mask.min.js'
				scriptMask.type = 'text/javascript'
				scriptMask.integrity = 'sha512-oJCa6FS2+zO3EitUSj+xeiEN9UTr+AjqlBZO58OPadb2RfqwxHpjTU8ckIC8F4nKvom7iru2s8Jwdo+Z8zm0Vg=='
				scriptMask.crossOrigin = 'anonymus'
				scriptMask.referrerpolicy = 'no-referrer'
				document.getElementsByTagName('head')[0].appendChild(scriptMask)
				scriptMask.onload = function () {
					resolve()
				}
			})
		},

		executeScript(script) {
			eval(script)
			this.executeCustomizerScripts()
			this.loadingPage = false
		},

		getClients() {
			this.loading = true
			juniperApi
				.get('/clients', { params: { all: true } })
				.then(({ data }) => {
					this.clients = data
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Get Client
		 * Function getting data for client from database
		 */
		async getClient() {
			this.loading = true
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => {
					this.client = data
					this.getClientThemeOptions(this.$route.params.id)
					this.getClientHeygovOptions(this.$route.params.id)
					this.getClientUserWayOptions(this.$route.params.id)
					this.getThemesList()
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		/**
		 * Get Themes List
		 * Function getting all themes list from Juniper
		 */
		getThemesList() {
			juniperApi
				.post('/themes/list')
				.then(({ data }) => {
					this.themes = data
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Get Theme Footers List
		 * Function getting all footer templates for given theme
		 */
		getThemeFootersList(theme) {
			juniperApi
				.post('/themes/' + theme + '/footers')
				.then(({ data }) => {
					this.footers = data
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Get Theme Headers List
		 * Function getting all header templates for given theme
		 */
		getThemeHeadersList(theme) {
			juniperApi
				.post('/themes/' + theme + '/headers')
				.then(({ data }) => {
					this.headers = data
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Get Theme Homepage Sections
		 * Function getting all homepage sections for given theme
		 */
		getThemeHomepageSections(theme, clientId) {
			return new Promise(resolve => {
				juniperApi
					.post('/themes/' + theme + '/homepage/sections', { client_id: clientId })
					.then(({ data }) => {
						this.homepageSections = data
						this.getClientHomepageSections()
						resolve(true)
					})
					.catch(error => {
						console.log(error)
					})
			})
		},

		/**
		 * Update Homepage Sections Order
		 * Function updates order of sections on homepage
		 */
		updateHomepageSectionsOrder() {
			const newList = [...this.homepageSections].map((item, index) => {
				const newSort = index + 1
				// also add in a new property called has changed if you want to style them / send an api call
				item.hasChanged = item.order !== newSort
				if (item.hasChanged) {
					item.order = newSort
				}
				return item
			})
			this.homepageSections = newList
		},

		/**
		 * Get Client Homepage Sections
		 * Function get all client homepage sections
		 */
		getClientHomepageSections() {
			this.homepageSections.forEach(hs => {
				let opt = this.themeOptions.theme_homepage_sections_json.find(s => s.code === hs.code)
				if (opt) {
					hs.order = opt.order
					hs.visible = opt.visible
				}
			})
			this.homepageSections.sort((a, b) => a.order - b.order)
			this.updateHomepageSectionsCss()
		},

		/**
		 * Save Client Theme Settings
		 * Function saves client theme settings (options) to database
		 */
		saveClientThemeSettings() {
			this.updateLoading = true
			let theme_homepage_sections_json = JSON.parse(JSON.stringify(this.homepageSections))
			theme_homepage_sections_json.forEach(s => {
				delete s.content
				delete s.id
				delete s.name
				delete s.hasChanged
				return s
			})

			let params = JSON.parse(JSON.stringify(this.themeOptions))

			// Home sections variables
			params.theme_homepage_sections_json = theme_homepage_sections_json

			let self = this
			// Footer Variables
			params.theme_footer_json = Object.keys(this.allFooters).reduce((acc, key) => {
				acc[key] = self.allFooters[key].variables
				return acc
			}, {})
			// Footer Pages
			params.theme_footer_pages = Object.keys(this.allFooters).reduce((acc, key) => {
				if (self.allFooters[key].pages && self.allFooters[key].pages.length) {
					acc[key] = self.allFooters[key].pages.map(page => page.value)
				}
				return acc
			}, {})
			// Header Variables
			params.theme_header_json = Object.keys(this.allHeaders).reduce((acc, key) => {
				acc[key] = self.allHeaders[key].variables
				return acc
			}, {})
			// Header Pages
			params.theme_header_pages = Object.keys(this.allHeaders).reduce((acc, key) => {
				if (self.allHeaders[key].pages && self.allHeaders[key].pages.length) {
					acc[key] = self.allHeaders[key].pages.map(page => page.value)
				}
				return acc
			}, {})
			// HeyGov Button
			params.heygov_button = this.heygovButton
			// UserWay Button
			params.userway_button = this.userwayButton

			juniperApi
				.post('/options/' + this.$route.params.id + '/save', { params: params })
				.then(({ data }) => {
					this.getClient()
					this.executeScript(data.js)
					this.updateLoading = false
					this.$swal.fire({
						icon: 'success',
						title: data.message,
						timer: 2000,
					})
				})
				.catch(error => {
					this.updateLoading = false
					console.log(error)
				})
		},

		/**
		 * Regenerate static content
		 * TODO: Based on changes regenerate only homepage or all pages
		 */
		generateStatic() {
			this.$swal
				.fire({
					title: 'Do you want to regenerate static content?',
					text: `All pages that have changes will be regenerated again! Note that unsaved changes will not apply to static!`,
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes, regenerate!',
					toast: false,
					timer: 0,
				})
				.then(result => {
					if (result.isConfirmed) {
						this.staticLoading = true
						juniperApi
							.post('/clients/' + this.$route.params.id + '/static/generate')
							.then(({ data }) => {
								this.$swal.fire({
									icon: 'success',
									title: data.message,
									timer: 4000,
								})
								this.staticLoading = false
							})
							.catch(error => {
								this.staticLoading = false
								console.log(error)
							})
					}
				})
		},

		/**
		 * Get Client Theme Options
		 * Function takes client theme options from database
		 */
		getClientThemeOptions(clientId) {
			juniperApi
				.post('/options/' + clientId, { selector: 'theme_' })
				.then(async ({ data }) => {
					this.themeOptions = data
					this.getClientThemeSettings(data.theme_active, clientId)
					this.updateThemeSettingsPreview()
					this.getThemeCss(data.theme_active)
					this.getThemeAssetsCss(data.theme_active)
					await this.getThemeHomepageSections(data.theme_active, clientId)
					this.getFooter(data.theme_active, clientId)
					this.getThemeFootersList(this.themeOptions.theme_active)
					this.getHeader(data.theme_active, clientId)
					this.getThemeHeadersList(this.themeOptions.theme_active)
				})
				.catch(error => {
					console.log(error)
				})
		},

		getClientHeygovOptions(clientId) {
			juniperApi
				.post('/options/' + clientId, { selector: 'heygov_' })
				.then(async ({ data }) => {
					if (data.heygov_button) {
						this.heygovButton = data.heygov_button
					}
				})
				.catch(error => {
					console.log(error)
				})
		},

		getClientUserWayOptions(clientId) {
			juniperApi
				.post('/options/' + clientId, { selector: 'userway_' })
				.then(async ({ data }) => {
					if (data.userway_button) {
						this.userwayButton = data.userway_button
					}
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Change Theme
		 * Function change theme
		 */
		changeTheme() {
			this.getThemeCss(this.themeOptions.theme_active)
			this.getThemeAssetsCss(this.themeOptions.theme_active)
			this.getThemeHomepageSections(this.themeOptions.theme_active, this.$route.params.id)
			this.getFooter(this.themeOptions.theme_active, this.$route.params.id)
			this.getThemeFootersList(this.themeOptions.theme_active)
			this.getHeader(this.themeOptions.theme_active, this.$route.params.id)
			this.getThemeHeadersList(this.themeOptions.theme_active)
		},

		// Open homepage section settings
		openHomepageSectionSettings(index) {
			this.homepageSections.forEach(sec => {
				sec.settings = false
			})
			this.homepageSections[index].settings = true
		},

		// Close homepage section settings
		closeHomepageSectionSettings() {
			this.homepageSections.forEach(sec => {
				sec.settings = false
			})
		},

		// Show homepage settings
		showSettings(type) {
			this.activeSidebarSettings = type
			this.sidebarMenu = false
		},

		// Back to sidebar menu
		backToSidebarMenu() {
			this.sidebarMenu = true
			this.activeSidebarSettings = ''
		},

		/**
		 * Get Theme CSS
		 * Function return theme css data
		 */
		getThemeCss(theme) {
			juniperApi
				.post('/themes/' + theme + '/css')
				.then(({ data }) => {
					this.themeCss = data
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Get Theme CSS
		 * Function return theme css data
		 */
		getThemeAssetsCss(theme) {
			juniperApi
				.post('/themes/' + theme + '/assets')
				.then(({ data }) => {
					this.assetsCss = data

					// Regular expression to match the pattern
					const regex = /custom-fonts(.*?)custom-fonts/
					// Extract the JSON string
					const match = data.match(regex)
					if (match) {
						try {
							// Parse the JSON string
							const jsonArray = JSON.parse(match[1])
							this.googleFonts = this.googleFonts.concat(jsonArray).sort()
							this.googleFonts = [...new Set(this.googleFonts)]
						} catch (error) {
							console.error('Error parsing JSON:', error)
						}
					}
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Get Header
		 * Function return header data
		 */
		getHeader(theme, clientId = null) {
			if (clientId == null) {
				clientId = this.$route.params.id
			}
			juniperApi
				.post('/themes/' + theme + '/header', {
					client_id: clientId,
					template: this.themeOptions.theme_header_active,
					customizer: true,
				})
				.then(({ data }) => {
					this.header = data[this.themeOptions.theme_header_active]
					this.allHeaders = data
					// eslint-disable-next-line
					this.themeJs = data[this.themeOptions.theme_header_active].js
					// this.loadScripts().then(() => {
					this.executeScript(data[this.themeOptions.theme_header_active].js)
					//})
					this.updateHeaderCss(this.header)
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Get Footer
		 * Function return footer data
		 */
		getFooter(theme, clientId = null) {
			if (clientId == null) {
				clientId = this.$route.params.id
			}
			juniperApi
				.post('/themes/' + theme + '/footer', {
					client_id: clientId,
					template: this.themeOptions.theme_footer_active,
				})
				.then(({ data }) => {
					this.footer = data[this.themeOptions.theme_footer_active]
					this.allFooters = data
					this.updateFooterCss(this.footer)
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Update Header CSS
		 * Update css file with css variables from header
		 */
		updateHeaderCss(header) {
			let headerCss = ''
			header.variables.forEach(variable => {
				if (variable.name.includes('_CSS')) {
					let cssAttributeName = variable.name.toLowerCase().substring(0, variable.name.indexOf('_'))
					let cssAttributeValue = variable.value
					let cssAttributeSelector = variable.selector || 'header'
					if (cssAttributeValue && cssAttributeValue !== '') {
						if (cssAttributeName === 'font-family' && !cssAttributeValue.startsWith('TownWeb ') && !cssAttributeValue.endsWith(' TownWeb')) {
							this.fontsCss += `@import url('https://fonts.googleapis.com/css?family=${cssAttributeValue}&display=swap');\n`
							headerCss += `${cssAttributeSelector} {\n ${cssAttributeName}:'${cssAttributeValue}';\n }\n`
						} else {
							if (cssAttributeSelector.includes('``@media (')) {
								cssAttributeSelector = cssAttributeSelector.replace('``', '')
								let cssAttributeSelectorArr = cssAttributeSelector.split('``')
								headerCss += `${cssAttributeSelectorArr[0]} {\n ${cssAttributeSelectorArr[1]} {\n ${cssAttributeName}:${cssAttributeValue};\n }\n }\n`
							} else {
								headerCss += `${cssAttributeSelector} {\n ${cssAttributeName}:${cssAttributeValue};\n }\n`
							}
						}
					}
				}
			})
			this.headerCss = headerCss
		},

		/**
		 * Update Footer CSS
		 * Update css file with css variables from footer
		 */
		updateFooterCss(footer) {
			let footerCss = ''
			footer.variables.forEach(variable => {
				if (variable.name.includes('_CSS')) {
					let cssAttributeName = variable.name.toLowerCase().substring(0, variable.name.indexOf('_'))
					let cssAttributeValue = variable.value
					let cssAttributeSelector = variable.selector || 'footer'
					if (cssAttributeValue && cssAttributeValue !== '') {
						if (cssAttributeName === 'font-family' && !cssAttributeValue.startsWith('TownWeb ') && !cssAttributeValue.endsWith(' TownWeb')) {
							if (!this.fontsCss.includes(cssAttributeValue)) {
								this.fontsCss += `@import url('https://fonts.googleapis.com/css?family=${cssAttributeValue}&display=swap');\n`
							}
							footerCss += `${cssAttributeSelector} {\n ${cssAttributeName}:'${cssAttributeValue}';\n }\n`
						} else {
							footerCss += `${cssAttributeSelector} {\n ${cssAttributeName}:${cssAttributeValue};\n }\n`
						}
					}
				}
			})
			this.footerCss = footerCss
		},

		/**
		 * Update Homepage Sections CSS
		 * Update css file with css variables from homepage sections
		 */
		updateHomepageSectionsCss() {
			let homepageCss = ''

			this.homepageSections.forEach(section => {
				section.variables.forEach(variable => {
					if (section.visible) {
						if (variable.name.includes('_CSS')) {
							let cssAttributeName = variable.name.toLowerCase().substring(0, variable.name.indexOf('_'))
							let cssAttributeValue = variable.value
							let cssAttributeSelector = variable.selector || 'footer'
							if (cssAttributeValue && cssAttributeValue !== '') {
								if (cssAttributeName === 'font-family' && !cssAttributeValue.startsWith('TownWeb ') && !cssAttributeValue.endsWith(' TownWeb')) {
									if (!this.fontsCss.includes(cssAttributeValue)) {
										this.fontsCss += `@import url('https://fonts.googleapis.com/css?family=${cssAttributeValue}&display=swap');\n`
									}
									homepageCss += `${cssAttributeSelector} {\n ${cssAttributeName}:'${cssAttributeValue}';\n }\n`
								} else {
									if (cssAttributeSelector.includes('``@media (')) {
										cssAttributeSelector = cssAttributeSelector.replace('``', '')
										let cssAttributeSelectorArr = cssAttributeSelector.split('``')
										homepageCss += `${cssAttributeSelectorArr[0]} {\n ${cssAttributeSelectorArr[1]} {\n ${cssAttributeName}:${cssAttributeValue};\n }\n }\n`
									} else {
										homepageCss += `${cssAttributeSelector} {\n ${cssAttributeName}:${cssAttributeValue};\n }\n`
									}
								}
							}
						}
					}
				})
			})

			this.homepageCss = homepageCss
		},

		getHomepageHTML(theme, homepage) {
			juniperApi
				.post('/themes/' + theme + '/homepage-html', {
					clientId: this.client.id,
					type: homepage,
					themeCss: this.themeCss,
					fontsCss: this.fontsCss,
					headerCss: this.headerCss,
					footerCss: this.footerCss,
					header: this.header,
					footer: this.footer,
					homepageSections: this.homepageSections,
				})
				.then(({ data }) => {
					this.websiteData = data
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Update preview
		 * Function updates html when property is changed
		 */
		updatePreview(e) {
			if (e.type === 'footer') {
				this.updateFooterPreview(this.footer)
			}
			if (e.type === 'header') {
				this.updateHeaderPreview(this.header)
			}
			if (e.type === 'homepage') {
				let section = this.homepageSections[e.sectionIndex]
				this.updateSectionPreview(section)
			}
			if (e.type === 'theme-settings') {
				this.updateThemeSettingsPreview()
			}
		},

		/**
		 * Update Header Preview
		 * Function return updated header content
		 */
		updateHeaderPreview(header) {
			juniperApi
				.post('/themes/' + this.themeOptions.theme_active + '/customizer/update-header-preview', {
					variables: header.variables,
					header: this.themeOptions.theme_header_active,
					clientId: this.client.id,
				})
				.then(({ data }) => {
					this.header.html = data
					this.updateHeaderCss(this.header)
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Update Section Content Preview
		 * Function returns updated section content
		 */
		updateSectionPreview(section) {
			juniperApi
				.post('/themes/' + this.themeOptions.theme_active + '/customizer/update-section-preview', {
					variables: section.variables,
					section: section.name,
					clientId: this.client.id,
				})
				.then(({ data }) => {
					section.content = data
					this.updateHomepageSectionsCss()
					this.executeScript(this.header.js)
				})
				.then(() => {
					this.executeCustomizerScripts()
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Update Footer Preview
		 * Function return updated footer content
		 */
		updateFooterPreview(footer) {
			juniperApi
				.post('/themes/' + this.themeOptions.theme_active + '/customizer/update-footer-preview', {
					variables: footer.variables,
					footer: this.themeOptions.theme_footer_active,
					clientId: this.client.id,
				})
				.then(({ data }) => {
					this.footer.html = data
					this.updateFooterCss(this.footer)
				})
				.catch(error => {
					console.log(error)
				})
		},

		/**
		 * Update Theme Settings Preview
		 * Function update root settings for theme with selected data
		 */
		updateThemeSettingsPreview() {
			let css = ':root{'
			let importFont = ''
			let figmaVariables = ''
			if (this.themeOptions && this.themeOptions.theme_settings_json) {
				this.themeOptions.theme_settings_json.forEach(setting => {
					let variableName = setting.code.toLowerCase().replaceAll('_', '-')
					let fontName = typeof setting.value === 'string' ? setting.value.replace(' ', '+') : null
					if (setting && setting.value && variableName.includes('font') && setting.value !== '' && !fontName.startsWith('TownWeb ') && !fontName.endsWith(' TownWeb')) {
						importFont +=
							"@import url('https://fonts.googleapis.com/css?family=" +
							fontName +
							":ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap');\n"
					}
					if (setting.code == 'FIGMA_VARIABLES') {
						figmaVariables = setting.value
					} else {
						css += '--' + variableName + ':' + setting.value + ';'
					}
				})
			}
			css += '}'
			css = importFont + figmaVariables + css
			this.settingsCss = css
		},

		// Insert photo from media library modal
		insertPhoto(photoUrl) {
			this.mediaElement.value = photoUrl
			this.$mediaModal.hide()
		},

		// Show Figma variables AI assistant modal
		showFigmaAiModal(setting) {
			this.figmaAiSetting = setting
			this.$figmaAiModal.show()
		},

		// Show media library modal
		showMediaModal(e) {
			this.mediaElement = e
			this.$mediaModal.show()
		},

		// Hide media library modal
		hideMediaModal() {
			this.$mediaModal.hide()
		},

		// Show select data modal
		showDataSelectModal(e) {
			this.dataSelectType = e.type
			this.dataSelectItem = e.item
			this.dataSelectSectionIndex = e.sectionIndex
			this.dataSelectSectionType = e.sectionType
			this.$dataSelectModal.show()
		},

		selectDataItem(e) {
			this.dataSelectItem.forEach(item => {
				if (e[item.name.toLowerCase()]) {
					item.value = e[item.name.toLowerCase()]
				}
			})
			this.updatePreview({ type: this.dataSelectSectionType, sectionIndex: this.dataSelectSectionIndex })
			this.hideDataSelectModal()
		},

		// Hide select data modal
		hideDataSelectModal() {
			this.$dataSelectModal.hide()
		},

		// Get Client Setting
		getClientThemeSettings(theme, clientId) {
			juniperApi
				.get('themes/' + theme + '/' + clientId + '/theme-settings')
				.then(({ data }) => {
					this.themeOptions.theme_settings_json = data
				})
				.catch(e => {
					console.log(e)
				})
		},

		selectCustomizerData(id) {
			this.getClientThemeOptions(id)
			/* this.updateFooterPreview(this.footer)
			this.updateHeaderPreview(this.header)
			this.homepageSections.forEach(sec => {
				this.updateSectionPreview(sec)
			})
			this.updateThemeSettingsPreview() */
		},

		applyChangedData() {
			this.updateFooterPreview(this.footer)
			this.updateHeaderPreview(this.header)
			this.homepageSections.forEach(sec => {
				this.updateSectionPreview(sec)
			})
			this.updateThemeSettingsPreview()
		},

		sidebarMinimize() {
			this.sidebarMinimized = !this.sidebarMinimized
		},

		handleHGButtonPosition() {
			if (this.heygovButton.position == 'bottom-right') {
				this.heygovButton.left = 'auto'
				this.heygovButton.right = '60px'
				this.heygovButton.bottom = '15px'
				this.heygovButton.top = 'auto'
			}

			if (this.heygovButton.position == 'bottom-left') {
				this.heygovButton.left = '15px'
				this.heygovButton.right = 'auto'
				this.heygovButton.bottom = '15px'
				this.heygovButton.top = 'auto'
			}

			if (this.heygovButton.position == 'top-right') {
				this.heygovButton.left = 'auto'
				this.heygovButton.right = '15px'
				this.heygovButton.bottom = 'auto'
				this.heygovButton.top = '15px'
			}

			if (this.heygovButton.position == 'top-left') {
				this.heygovButton.left = '15px'
				this.heygovButton.right = 'auto'
				this.heygovButton.bottom = 'auto'
				this.heygovButton.top = '15px'
			}
		},

		handleUWButtonPosition() {
			if (this.userwayButton.position == 'bottom-right') {
				this.userwayButton.left = 'auto'
				this.userwayButton.right = '15px'
				this.userwayButton.bottom = '15px'
				this.userwayButton.top = 'auto'
			}

			if (this.userwayButton.position == 'bottom-left') {
				this.userwayButton.left = '15px'
				this.userwayButton.right = 'auto'
				this.userwayButton.bottom = '15px'
				this.userwayButton.top = 'auto'
			}

			if (this.userwayButton.position == 'top-right') {
				this.userwayButton.left = 'auto'
				this.userwayButton.right = '15px'
				this.userwayButton.bottom = 'auto'
				this.userwayButton.top = '15px'
			}

			if (this.userwayButton.position == 'top-left') {
				this.userwayButton.left = '15px'
				this.userwayButton.right = 'auto'
				this.userwayButton.bottom = 'auto'
				this.userwayButton.top = '15px'
			}
		},
		limitSearchPagesText(count) {
			return `and ${count} other countries`
		},
		async getAlternativeHeaderPages(query) {
			let pages = await this.getPages(query)
			this.alternativeHeaderPages = pages.data.map(page => ({
				value: page.id,
				label: page.title,
			}))
		},
		async getAlternativeFooterPages(query) {
			let pages = await this.getPages(query)
			this.alternativeFooterPages = pages.data.map(page => ({
				value: page.id,
				label: page.title,
			}))
		},
		async getPages(query) {
			let pages = await juniperApi.get(`/pages?client_id=${this.$route.params.id}&per_page=5&search=${query}`)
			return pages
		},
	},
}
</script>
<style scoped>
.clients-wrapper {
	max-height: 600px;
	overflow: auto;
}
.client-data {
	display: flex;
	justify-content: start;
	align-items: center;
}

.heygov-widget-button {
	position: fixed;
	bottom: 25px;
	right: 25px;
	z-index: 500;
	background-color: #fcab1c;
	border-radius: 10px;
	box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.01);
	padding: 8px 20px;
	font-weight: 500;
	color: #5e81f4;
	cursor: pointer;
	transition: transform 0.2s ease-out;
}

.userway-button {
	border-radius: 50%;
	background-color: #0048ff;
	width: 44px;
	height: 44px;
	position: fixed;
	position: fixed;
	bottom: 15px;
	right: 15px;
	z-index: 500;
	cursor: pointer;
}
#alternativeHeaderSelected {
	background-color: rgba(0, 0, 0, 0.06);
	padding: 3px;
}
.custom__tag {
	display: inline-block;
	padding: 3px 12px;
	background: #d2d7ff;
	margin-right: 8px;
	margin-bottom: 8px;
	border-radius: 10px;
	cursor: pointer;
}
.custom__tag:nth-child(even) {
	background: #daffee;
}
</style>
